import { get } from 'lodash';

import {
  type MaasGroupSpec,
  type MaasGroupedWorkloadSpec,
  MaasRunMode,
  type MaasSubmitError,
} from '@/delivery-type-plugin/capabilities/maas-init/maasTypes';
import { parseFormJsonField } from '@/delivery-type-plugin/capabilities/maas-init/utils/jsonFormField';
import { resolveModelInstancePdSeparation } from '@/delivery-type-plugin/capabilities/maas-model-storage/modelFormUtils';
import type {
  MaasInferenceTopologySpec,
  ModelInstanceFormValues,
  ModelInstanceSpec,
} from '@/delivery-type-plugin/capabilities/maas-model-storage/modelInstanceTypes';
import { GATEWAY_NODES_FIELD } from '@/delivery-type-plugin/capabilities/maas-node-groups/nodeFieldNames';
import { parseNodesField } from '@/delivery-type-plugin/capabilities/maas-node-groups/nodeFields';
import {
  hasEmbeddedNodesInferenceImage,
  hasMaasInferenceSystemImage,
  nodeHasInferenceSystemImage,
  validateEmbeddedNodesSystemDiskForSubmit,
  validateMaasGroupedResourcesForSubmit,
  validateMaasRunModeTopology,
} from '@/delivery-type-plugin/capabilities/maas-node-groups/submitValidation';

import { isMiGatewayModuleEnabled } from '../gatewayModule';
import { isMiKvOffloadModuleEnabled } from '../kvOffloadModule';

/** 表单是否已选推理框架/自定义镜像（提交时会合并进各推理节点） */
export function formHasResolvableInferenceImage(values: ModelInstanceFormValues): boolean {
  if (hasMaasInferenceSystemImage(values)) return true;
  const frameworkImage = parseFormJsonField<Record<string, unknown>>(values.inferenceFrameworkImage) ?? values.inferenceFrameworkImage;
  if (frameworkImage && typeof frameworkImage === 'object') {
    return !!String(frameworkImage.url ?? frameworkImage.image ?? frameworkImage.imageKey ?? '').trim();
  }
  return false;
}

function segmentInferenceImageSatisfied(
  segment: Pick<ModelInstanceFormValues, 'runMode' | 'masterNodes' | 'workerNodes'>,
  values: ModelInstanceFormValues,
): boolean {
  if (hasEmbeddedNodesInferenceImage(segment)) return true;
  if (!formHasResolvableInferenceImage(values)) return false;
  const mode = segment.runMode || values.runMode || MaasRunMode.Normal;
  const masterNodes = parseNodesField(segment.masterNodes);
  const workerNodes = parseNodesField(segment.workerNodes);
  if (mode === MaasRunMode.PrimarySecondary) {
    return masterNodes.length > 0 && workerNodes.length > 0;
  }
  return workerNodes.length > 0;
}

function collectTopologyGroups(topology?: { headGroupSpec?: MaasGroupSpec; workerGroupSpecs?: MaasGroupSpec[] }): MaasGroupSpec[] {
  if (!topology) return [];
  return [...(topology.headGroupSpec ? [topology.headGroupSpec] : []), ...(topology.workerGroupSpecs ?? [])];
}

/** 将 inference 下所有组摊平供 GPU / 资源校验使用 */
export function flattenModelInstanceInferenceForValidation(
  spec: ModelInstanceSpec,
): Pick<MaasGroupedWorkloadSpec, 'headGroupSpec' | 'workerGroupSpecs' | 'gateway'> {
  const workers: MaasGroupSpec[] = [];
  const inf = spec.inference;
  if (inf?.prefillDecode) {
    workers.push(...collectTopologyGroups(inf.prefillDecode.prefill), ...collectTopologyGroups(inf.prefillDecode.decode));
  } else if (inf?.normal) {
    workers.push(...collectTopologyGroups(inf.normal));
  } else if (inf?.primarySecondary) {
    workers.push(...collectTopologyGroups(inf.primarySecondary));
  } else {
    const flatWorkers = [...(spec.workerGroupSpecs ?? [])];
    if (spec.kvOffload) flatWorkers.push(...collectTopologyGroups(spec.kvOffload));
    return {
      headGroupSpec: spec.headGroupSpec,
      workerGroupSpecs: flatWorkers,
      gateway: spec.gateway,
    };
  }
  if (spec.kvOffload) {
    workers.push(...collectTopologyGroups(spec.kvOffload));
  }
  return { headGroupSpec: undefined, workerGroupSpecs: workers, gateway: spec.gateway };
}

function collectNodeGroupNamesFromSegment(runMode: MaasRunMode | string | undefined, masterNodes: unknown, workerNodes: unknown): string[] {
  const mode = runMode || MaasRunMode.Normal;
  const masters = parseNodesField(masterNodes);
  const workers = parseNodesField(workerNodes);
  const nodes = mode === MaasRunMode.PrimarySecondary ? [...masters, ...workers] : workers;
  return nodes.map((node) => String(node?.name ?? '').trim()).filter(Boolean);
}

/** 网关 + 各推理拓扑节点组名称全局唯一（含 Prefill 与 Decode 跨段） */
export function validateModelInstanceUniqueNodeGroupNames(values: ModelInstanceFormValues): MaasSubmitError | undefined {
  const names: string[] = [];
  if (isMiGatewayModuleEnabled(values)) {
    const gateway = parseNodesField(get(values, GATEWAY_NODES_FIELD) ?? values.gatewayNodes)[0];
    const gatewayName = String(gateway?.name ?? '').trim();
    if (gatewayName) names.push(gatewayName);
  }
  if (resolveModelInstancePdSeparation(values)) {
    names.push(
      ...collectNodeGroupNamesFromSegment(values.prefillRunMode, values.prefillMasterNodes, values.prefillWorkerNodes),
      ...collectNodeGroupNamesFromSegment(values.decodeRunMode, values.decodeMasterNodes, values.decodeWorkerNodes),
    );
  } else {
    names.push(...collectNodeGroupNamesFromSegment(values.runMode, values.masterNodes, values.workerNodes));
  }
  if (isMiKvOffloadModuleEnabled(values)) {
    names.push(...collectNodeGroupNamesFromSegment(values.kvOffloadRunMode, values.kvOffloadMasterNodes, values.kvOffloadWorkerNodes));
  }
  const counts = new Map<string, number>();
  for (const name of names) {
    counts.set(name, (counts.get(name) ?? 0) + 1);
  }
  const duplicates = [...counts.entries()].filter(([, count]) => count > 1).map(([name]) => name);
  if (!duplicates.length) return undefined;
  return {
    key: 'data-center:maas.node.group.name.duplicate.global',
    fieldKey: 'data-center:maas.inference.node.config',
    params: { names: duplicates.join('、') },
  };
}

export function validateModelInstanceInferenceTopology(
  values: ModelInstanceFormValues,
  spec: ModelInstanceSpec,
): MaasSubmitError | undefined {
  if (resolveModelInstancePdSeparation(values)) {
    const segments: { runMode: ModelInstanceFormValues['prefillRunMode']; topology?: MaasInferenceTopologySpec }[] = [
      { runMode: values.prefillRunMode, topology: spec.inference?.prefillDecode?.prefill },
      { runMode: values.decodeRunMode, topology: spec.inference?.prefillDecode?.decode },
    ];
    for (const segment of segments) {
      const error = validateMaasRunModeTopology({
        runMode: segment.runMode,
        headGroupSpec: segment.topology?.headGroupSpec,
        workerGroupSpecs: segment.topology?.workerGroupSpecs,
      });
      if (error) return error;
    }
    return undefined;
  }
  const normal = spec.inference?.normal;
  if (normal?.workerGroupSpecs?.length) {
    return validateMaasRunModeTopology({
      runMode: values.runMode,
      headGroupSpec: undefined,
      workerGroupSpecs: normal.workerGroupSpecs,
    });
  }
  const ps = spec.inference?.primarySecondary;
  return validateMaasRunModeTopology({
    runMode: values.runMode,
    headGroupSpec: ps?.headGroupSpec,
    workerGroupSpecs: ps?.workerGroupSpecs,
  });
}

/** 勾选缓存节点时校验部署模式、系统盘与镜像 */
export function validateModelInstanceKvOffload(values: ModelInstanceFormValues, spec: ModelInstanceSpec): MaasSubmitError | undefined {
  if (!isMiKvOffloadModuleEnabled(values)) return undefined;
  const kv = spec.kvOffload;
  const topologyError = validateMaasRunModeTopology({
    runMode: values.kvOffloadRunMode,
    headGroupSpec: kv?.headGroupSpec,
    workerGroupSpecs: kv?.workerGroupSpecs,
  });
  if (topologyError) return topologyError;
  const diskError = validateEmbeddedNodesSystemDiskForSubmit({
    runMode: values.kvOffloadRunMode,
    masterNodes: values.kvOffloadMasterNodes,
    workerNodes: values.kvOffloadWorkerNodes,
  });
  if (diskError) return diskError;
  if (
    !segmentInferenceImageSatisfied(
      {
        runMode: values.kvOffloadRunMode,
        masterNodes: values.kvOffloadMasterNodes,
        workerNodes: values.kvOffloadWorkerNodes,
      },
      values,
    )
  ) {
    return { key: 'data-center:maas.inference.image.required' };
  }
  return undefined;
}

export function validateModelInstanceEmbeddedSystemDisk(values: ModelInstanceFormValues): MaasSubmitError | undefined {
  if (!resolveModelInstancePdSeparation(values)) {
    return validateEmbeddedNodesSystemDiskForSubmit(values);
  }
  const prefillError = validateEmbeddedNodesSystemDiskForSubmit({
    runMode: values.prefillRunMode,
    masterNodes: values.prefillMasterNodes,
    workerNodes: values.prefillWorkerNodes,
  });
  if (prefillError) return prefillError;
  return validateEmbeddedNodesSystemDiskForSubmit({
    runMode: values.decodeRunMode,
    masterNodes: values.decodeMasterNodes,
    workerNodes: values.decodeWorkerNodes,
  });
}

/** 勾选网关且已配网关组时：组内镜像或全局推理框架镜像任一满足即可 */
export function gatewayInferenceImageSatisfied(values: ModelInstanceFormValues, gateway?: MaasGroupSpec): boolean {
  if (!gateway) return false;
  if (nodeHasInferenceSystemImage(gateway)) return true;
  return formHasResolvableInferenceImage(values);
}

export { resolveModelInstancePdSeparation } from '@/delivery-type-plugin/capabilities/maas-model-storage/modelFormUtils';

export function hasModelInstanceEmbeddedInferenceImage(values: ModelInstanceFormValues): boolean {
  if (!resolveModelInstancePdSeparation(values)) {
    return segmentInferenceImageSatisfied(values, values);
  }
  return (
    segmentInferenceImageSatisfied(
      {
        runMode: values.prefillRunMode,
        masterNodes: values.prefillMasterNodes,
        workerNodes: values.prefillWorkerNodes,
      },
      values,
    ) &&
    segmentInferenceImageSatisfied(
      {
        runMode: values.decodeRunMode,
        masterNodes: values.decodeMasterNodes,
        workerNodes: values.decodeWorkerNodes,
      },
      values,
    )
  );
}

export function validateModelInstanceGroupedResources(spec: ModelInstanceSpec): MaasSubmitError | undefined {
  const flat = flattenModelInstanceInferenceForValidation(spec);
  return validateMaasGroupedResourcesForSubmit(flat);
}
